/**
 * Images management page component
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { ImageCard } from '../components/ImageCard';
import { FileUpload } from '../components/FileUpload';
import { Loading } from '../components/Loading';
import { useImages } from '../hooks/useImages';
import type { UploadResponse } from '../types';

export function Images() {
  const navigate = useNavigate();
  const { images, isLoading, total, page, totalPages, fetchImages, uploadImage, deleteImage, getShareUrl } = useImages();
  const [showUpload, setShowUpload] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);

  useEffect(() => {
    fetchImages(1);
  }, [fetchImages]);

  const handleUpload = useCallback(async (file: File, originalName: string) => {
    const result = await uploadImage(file, originalName);
    if (result.success) {
      setToast({ message: 'Image uploaded successfully!', type: 'success' });
      setShowUpload(false);
    } else {
      setToast({ message: result.error || 'Upload failed', type: 'error' });
    }
    return result;
  }, [uploadImage]);

  const handleDelete = useCallback(async (id: string) => {
    const result = await deleteImage(id);
    if (result.success) {
      setToast({ message: 'Image deleted successfully!', type: 'success' });
    } else {
      setToast({ message: result.error || 'Delete failed', type: 'error' });
    }
    return result;
  }, [deleteImage]);

  const handleShare = useCallback(async (id: string) => {
    const result = await getShareUrl(id);
    if (result.success && result.url) {
      const fullUrl = window.location.origin + result.url;
      navigator.clipboard.writeText(fullUrl);
      setToast({ message: 'Share URL copied to clipboard!', type: 'success' });
    } else {
      setToast({ message: result.error || 'Failed to get share URL', type: 'error' });
    }
  }, [getShareUrl]);

  const handleImageClick = useCallback((id: string) => {
    navigate(`/images/${id}`);
  }, [navigate]);

  const handlePageChange = useCallback((newPage: number) => {
    fetchImages(newPage);
  }, [fetchImages]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-white">Images</h2>
            <p className="text-sm text-gray-400 mt-1">
              {total} {total === 1 ? 'image' : 'images'} total
            </p>
          </div>
          <button
            onClick={() => setShowUpload(!showUpload)}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {showUpload ? 'Cancel' : 'Upload Image'}
          </button>
        </div>

        {/* Upload Section */}
        {showUpload && (
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-medium text-white mb-4">Upload New Image</h3>
            <FileUpload onUpload={handleUpload} />
          </div>
        )}

        {/* Toast */}
        {toast && (
          <div className={`p-4 rounded-lg border ${
            toast.type === 'success' 
              ? 'bg-green-900/50 border-green-700' 
              : 'bg-red-900/50 border-red-700'
          }`}>
            <p className={toast.type === 'success' ? 'text-green-300' : 'text-red-300'}>
              {toast.message}
            </p>
          </div>
        )}

        {/* Images Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loading size="lg" text="Loading images..." />
          </div>
        ) : images.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {images.map((image) => (
                <ImageCard
                  key={image.id}
                  image={image}
                  onDelete={handleDelete}
                  onShare={handleShare}
                  onClick={handleImageClick}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  Previous
                </button>
                <span className="text-gray-400 px-4">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === totalPages}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <svg className="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <h3 className="text-lg font-medium text-white mb-2">No images yet</h3>
            <p className="text-gray-400 mb-6">Upload your first image to get started</p>
            <button
              onClick={() => setShowUpload(true)}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
            >
              Upload Image
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Images;
